import { useWizardUploadController } from '@/hooks/useWizardUploadController'
import { FileSelectSubStep } from './FileSelectSubStep'
import { PlatformConfirmDialog } from './PlatformConfirmDialog'
import { FieldMappingStep } from '@/components/upload/workflow/FieldMappingStep'
import { DriverReviewStep } from '@/components/upload/workflow/DriverReviewStep'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, Loader2, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface WizardUploadContentProps {
  periodId: string
  onComplete: () => void
}

export function WizardUploadContent({ periodId, onComplete }: WizardUploadContentProps) {
  const controller = useWizardUploadController(periodId)
  const { state } = controller

  return (
    <div className="space-y-6">
      {/* Error display */}
      {state.error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{state.error}</AlertDescription>
        </Alert>
      )}

      {/* Platform confirmation dialog */}
      <PlatformConfirmDialog
        open={state.showPlatformDialog}
        suggestedPlatform={state.detectedPlatform}
        confidence={state.platformConfidence}
        onConfirm={controller.confirmPlatform}
        onCancel={controller.cancelPlatformSelection}
      />

      {/* File select sub-step */}
      {state.subStep === 'file-select' && (
        <FileSelectSubStep
          existingUploads={state.existingUploads}
          isLoading={state.existingUploadsLoading || state.isLoading}
          onFileSelect={controller.handleFileUpload}
          onDeleteUpload={controller.deleteUpload}
          onContinue={onComplete}
          canContinue={controller.canContinueToAdjustments()}
        />
      )}

      {/* Field mapping sub-step */}
      {state.subStep === 'field-mapping' && state.uploadStaging && state.currentUpload && (
        <div className="space-y-4">
          <FieldMappingStep
            upload={state.currentUpload}
            identifierMapping={state.identifierMapping}
            fieldMapping={state.fieldMapping}
            onIdentifierMappingChange={(mapping) => controller.updateState({ identifierMapping: mapping })}
            onFieldMappingChange={(mapping) => controller.updateState({ fieldMapping: mapping })}
          />
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => controller.goToSubStep('file-select')}
            >
              Tillbaka
            </Button>
            <Button
              onClick={controller.runFieldMapping}
              disabled={state.isLoading}
            >
              {state.isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Bearbetar...
                </>
              ) : (
                'Fortsätt'
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Driver review sub-step */}
      {state.subStep === 'driver-review' && state.uploadStaging && (
        <div className="space-y-4">
          <DriverReviewStep
            upload={state.currentUpload || undefined}
            driverMatches={state.driverMatches}
            availableDrivers={state.availableDrivers}
            unmatchedActions={state.unmatchedActions}
            perfectMatchOverrides={state.perfectMatchOverrides}
            ignoredRows={state.ignoredRows}
            autoAcceptMatches={state.autoAcceptMatches}
            onUnmatchedActionChange={controller.onUnmatchedActionChange}
            onPerfectMatchOverrideChange={controller.onPerfectMatchOverrideChange}
            onRemovePerfectMatchOverride={controller.onRemovePerfectMatchOverride}
            onIgnoreRow={controller.onIgnoreRow}
            onUnignoreRow={controller.onUnignoreRow}
            onAutoAcceptMatchesChange={controller.onAutoAcceptMatchesChange}
          />
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => controller.goToSubStep('field-mapping')}
            >
              Tillbaka
            </Button>
            <Button
              onClick={controller.processDriverActionsAndCreateEarnings}
              disabled={state.isLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {state.isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Skapar inkomster...
                </>
              ) : (
                'Spara inkomster'
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Creating earnings sub-step */}
      {state.subStep === 'creating-earnings' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Skapar inkomster...
            </CardTitle>
          </CardHeader>
          <CardContent>
            {state.processingProgress && (
              <div className="space-y-2">
                <Progress value={state.processingProgress.percentage} />
                <p className="text-sm text-muted-foreground text-center">
                  {state.processingProgress.status}
                  {' '}({state.processingProgress.current}/{state.processingProgress.total})
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Complete sub-step */}
      {state.subStep === 'complete' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Uppladdning klar!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {state.earningsResults && (
              <div className="text-sm text-muted-foreground">
                <p>{state.earningsResults.successCount} inkomster skapade</p>
                {state.earningsResults.errorCount > 0 && (
                  <p className="text-destructive">
                    {state.earningsResults.errorCount} fel uppstod
                  </p>
                )}
              </div>
            )}
            <div className="flex gap-4">
              <Button variant="outline" onClick={controller.startNewUpload}>
                Ladda upp fler filer
              </Button>
              <Button onClick={onComplete}>
                Fortsätt till justeringar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
